import { useEffect, useRef, useState } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import { Avatar } from "./ui";
import { ChevronDownIcon, LogoutIcon, ShieldIcon, UserCircleIcon } from "./icons";

// The top-right chip: full name over username, with the initials badge.
// Clicking it opens the account menu.
export default function UserMenu() {
  const { auth } = usePage().props;
  const user = auth?.user;
  const [open, setOpen] = useState(false);
  const wrapper = useRef(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event) {
      if (!wrapper.current?.contains(event.target)) setOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!user) return null;

  const items = [
    { label: "My Profile", href: "/profile", icon: UserCircleIcon },
    { label: "Security", href: "/profile?tab=security", icon: ShieldIcon },
  ];

  return (
    <div className="relative" ref={wrapper}>
      <button
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition hover:bg-brand-50 lg:gap-3 lg:px-3"
      >
        {/* Named from the width the header has room to name things. Between a phone and a
            desktop the top bar carries the quick actions across its middle, and the name
            here was one of the two blocks they ran into — it is a label on a control whose
            own face already identifies it, so it is what gives way. */}
        <span className="hidden min-w-0 text-right leading-tight lg:block">
          <span className="block truncate text-sm font-semibold text-slate-800">
            {user.full_name}
          </span>
          <span className="block truncate text-xs text-slate-500">{user.username}</span>
        </span>
        <Avatar initials={user.initials} src={user.avatar_path} alt={user.full_name} />
        <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-lg animate-pop-in"
        >
          {/* Wherever the chip hides the name, repeat it in the menu — so it is never
              nowhere. Kept in step with the chip above: the two breakpoints are one rule. */}
          <div className="border-b border-slate-100 px-4 pb-2.5 pt-1.5 lg:hidden">
            <p className="truncate text-sm font-semibold text-slate-800">{user.full_name}</p>
            <p className="truncate text-xs text-slate-500">{user.username}</p>
          </div>

          {items.map(({ label, href, icon: ItemIcon }) => (
            <Link
              key={href}
              href={href}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
            >
              <ItemIcon className="h-5 w-5 text-slate-400" />
              {label}
            </Link>
          ))}

          <div className="my-1.5 border-t border-slate-100" />

          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              router.delete("/logout");
            }}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition hover:bg-slate-50"
          >
            <LogoutIcon className="h-5 w-5 text-slate-400" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
